import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  Accordion,
  AccordionSummary,
  Box,
  ClickAwayListener,
  Divider,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Editor, Transforms } from 'slate';
import { BpCheckbox } from '../../../../components/mui/BpCheckbox';
import { CustomButton } from '../../../../components/mui/CustomButton';
import { CustomFormControlLabel } from '../../../../components/mui/CustomFormControlLabel';
import CustomTextField from '../../../../components/mui/CustomTextField';
import { getSentenceAroundHighlightedWordWithTag } from '../../../../utils/getSentenceAroundHighlightedWordWithTag';
import { updateScrollToIndexValue, updateSlateData } from '../actions/slateDataActions';
import { updateFindAndReplaceState } from '../actions/findAndReplaceActions';
import debounce from 'lodash/debounce';
import { escapeRegExp } from 'lodash';
import { trackEvent } from '../../../../utils/ga';

const FindAndReplaceModal = ({ onClose, editor }) => {
  const dispatch = useDispatch();
  const {
    wordSearchedId,
    mode: reduxMode,
    replaceText,
    isFillerWords,
    isMatchFullWord,
    isCaseSensitive,
    isCommonErrors,
  } = useSelector((state) => state.findAndReplace);
  const itemRefs = useRef({});
  const debouncedUpdateRef = useRef(
    debounce((words) => {
      trackEvent('find_text', {
        params: JSON.stringify({
          find_text: words.join(' '),
          mode: mode,
        }),
      });
      dispatch(updateFindAndReplaceState({ word: words }));
    }, 800),
  );
  const commonErrorsOptionRef = useRef('0');
  const findTextArrayRef = useRef([]);
  const [isButtonsDisable, setIsButtonDisable] = useState(false);
  const [findText, setFindText] = useState('');
  const [replacedTextArray, setReplacedTextArray] = useState([]);
  const isReplaceTextDisabledRef = useRef(false);
  const [fillerWords, setFillterWords] = useState(['ah', 'uh', 'um']);
  const [activeIndex, setActiveIndex] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [timeoutId, setTimeoutId] = useState(null);
  const originalScrollRef = useRef(null);
  const [mode, setMode] = useState(reduxMode || 'string');

  const handleClose = () => {
    setFindText('');
    setFillterWords(['ah', 'uh', 'um']);
    dispatch(
      updateFindAndReplaceState({
        mode: 'string',
        selectedIndex: null,
        isCommonErrors: false,
        isCaseSensitive: false,
        word: [],
        replaceText: '',
        isFillerWords: false,
        isMatchFullWord: false,
      }),
    );
    setMode('string');
    onClose();
  };

  const handleFillerWordsChange = (event) => {
    setFillterWords(['ah', 'uh', 'um']);
    dispatch(
      updateFindAndReplaceState({
        isCommonErrors: false,
        word: event.target.checked ? fillerWords : [],
        replaceText: '',
        isFillerWords: event.target.checked,
        isMatchFullWord: false,
      }),
    );
    findTextArrayRef.current = [];
    setReplacedTextArray([]);
    commonErrorsOptionRef.current = '0';
    if (event.target.checked) {
      setFindText(fillerWords.join(' '));
      isReplaceTextDisabledRef.current = true;
    } else {
      setFindText('');
      isReplaceTextDisabledRef.current = false;
    }
  };

  const handleCommonErrorsChange = (event) => {
    dispatch(
      updateFindAndReplaceState({
        isCommonErrors: event.target.checked,
        isCaseSensitive: false,
        word: [],
        replaceText: '',
        isFillerWords: false,
        isMatchFullWord: false,
      }),
    );
    setFindText('');
    findTextArrayRef.current = [];
    setReplacedTextArray([]);
    commonErrorsOptionRef.current = '0';
    isReplaceTextDisabledRef.current = false;
  };

  const handleClickReplace = (index) => {
    setActiveIndex(index); // Set the clicked index as active
  };

  const handleReplace = (id) => {
    if (!editor) return;

    const match = id.match(/-\((\d+)\)/);
    const occurrenceCount = match ? parseInt(match[1], 10) : null;
    const baseId = id.replace(/-\(\d+\)/, '');
    let occurrenceTracker = 0;

    const nodes = Array.from(
      Editor.nodes(editor, {
        at: [],
        match: (node) => node.id === baseId,
      }),
    );

    const [node, path] = nodes[0];

    const fullText = node.children.map((child) => child.text || '').join('');

    let updatedFullText = fullText;
    if (isFillerWords) {
      fillerWords.forEach((fillerWord) => {
        const fillerWordRegex = new RegExp(`\\b${fillerWord}\\b`, isCaseSensitive ? 'g' : 'gi');
        updatedFullText = updatedFullText.replace(fillerWordRegex, (match) => {
          occurrenceTracker += 1;
          if (occurrenceTracker === occurrenceCount) {
            return '';
          }
          return match;
        });
      });
    } else {
      const regex = new RegExp(escapeRegExp(findText), isCaseSensitive ? 'g' : 'gi');
      updatedFullText = updatedFullText.replace(regex, (match) => {
        occurrenceTracker += 1;
        if (occurrenceTracker === occurrenceCount) {
          return replaceText;
        }
        return match;
      });
    }

    let updatedWords = updatedFullText.match(/\S+\s*/g) || [];
    let wordIndex = 0;

    const updatedChildren = node.children.map((child) => {
      if (wordIndex >= updatedWords.length) {
        return { ...child, text: '' };
      }

      const updatedTextPart = updatedWords[wordIndex];
      wordIndex += 1;

      if (updatedTextPart === child.text) {
        return child;
      }

      return { ...child, text: updatedTextPart };
    });

    Transforms.removeNodes(editor, { at: path });
    Transforms.insertNodes(editor, { ...node, children: updatedChildren }, { at: path });

    dispatch(updateSlateData(editor.children));

    findTextArrayRef.current = findTextArrayRef.current.filter((item) => item.uniqueId !== id);
    setReplacedTextArray((prev) => prev.filter((item) => item.id !== id));
    setIsButtonDisable(true);

    if (findTextArrayRef.current.length > 0) {
      const nextIndex =
        activeIndex < findTextArrayRef.current.length ? activeIndex : findTextArrayRef.current.length - 1;
      setActiveIndex(nextIndex);
      setActiveId(findTextArrayRef.current[nextIndex].uniqueId);
      dispatch(updateScrollToIndexValue(findTextArrayRef.current[nextIndex].index));
    } else {
      setActiveIndex(null);
      setActiveId(null);
    }

    trackEvent('find_and_replace', {
      params: JSON.stringify({
        find_text: findText,
        replace_text: replaceText,
        is_filler_words: isFillerWords,
        is_case_sensitive: isCaseSensitive,
        is_match_full_word: isMatchFullWord,
        is_common_errors: isCommonErrors,
        mode: mode,
      }),
    });
  };

  const handleReplaceAll = () => {
    if (!editor) return;

    const nodes = Array.from(
      Editor.nodes(editor, {
        at: [],
        match: (node) => findTextArrayRef.current.some((match) => match.id.replace(/-\(\d+\)/, '') === node.id),
      }),
    );

    nodes.forEach(([node, path]) => {
      const fullText = node.children.map((child) => child.text || '').join('');

      const escapedFindText = findText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = isFillerWords
        ? new RegExp(`\\b(${fillerWords.join('|')})\\b`, isCaseSensitive ? 'g' : 'gi')
        : new RegExp(escapedFindText, isCaseSensitive ? 'g' : 'gi');

      const safeReplace = () => (isFillerWords ? '' : replaceText);
      const updatedFullText = fullText.replace(regex, safeReplace);

      let remainingText = updatedFullText;
      const updatedChildren = node.children.map((child) => {
        if (!child.text) return child;

        const originalWords = child.text.split(/(\s+)/);
        const updatedWords = [];
        let remainingWords = remainingText.split(/(\s+)/);

        for (let i = 0; i < originalWords.length && remainingWords.length > 0; i++) {
          const word = remainingWords.shift();
          updatedWords.push(word);
        }

        const updatedTextPart = updatedWords.join('');

        if (updatedTextPart === child.text) {
          remainingText = remainingWords.join('');
          return child;
        }

        remainingText = remainingWords.join('');
        return { ...child, text: updatedTextPart };
      });

      Transforms.removeNodes(editor, { at: path });
      Transforms.insertNodes(editor, { ...node, children: updatedChildren }, { at: path });
    });

    trackEvent('find_and_replace_all', {
      params: JSON.stringify({
        find_text: findText,
        replace_text: replaceText,
        is_filler_words: isFillerWords,
        is_case_sensitive: isCaseSensitive,
        is_match_full_word: isMatchFullWord,
        is_common_errors: isCommonErrors,
        mode: mode,
      }),
    });

    dispatch(updateSlateData(editor.children));
    handleClose();
  };

  const handleFindWordChange = (e) => {
    const value = e.target.value;
    setFindText(value);

    if (isFillerWords) {
      setFillterWords(value.split(' ').filter((word) => word.trim() !== ''));
      debouncedUpdateRef.current(value.split(' ').filter((word) => word.trim() !== ''));
    } else {
      debouncedUpdateRef.current([value]);
    }
  };

  const handleChangeCommonErrors = (e) => {
    setFindText(e.target.value);
    dispatch(updateFindAndReplaceState({ word: [e.target.value] }));
    commonErrorsOptionRef.current = e.target.value;
  };

  const extractSentencesWithTargetWord = (data, targetWord, replaceWord = '') => {
    const sentences = [];
    const replaces = [];
    const regexFlags = isCaseSensitive ? 'g' : 'gi';

    if (isFillerWords && !fillerWords.length) {
      return sentences;
    }

    // Prepare regex for target word
    const wordsToFind = isFillerWords ? fillerWords : [targetWord];
    const escapedWords = wordsToFind.map((word) => escapeRegExp(word));
    const combinedRegex =
      isMatchFullWord || isFillerWords
        ? new RegExp(`(?<=^|\\s)(${escapedWords.join('|')})(?=\\s|$)`, regexFlags)
        : new RegExp(`(${escapedWords.join('|')})`, regexFlags);

    data.forEach((item, index) => {
      const sentence = item.children.map((child) => child.text).join('');

      // Find all matches of the target word in the sentence
      const matches = sentence.match(combinedRegex);

      if (matches && matches.length > 0) {
        // Generate variations for each occurrence
        matches.forEach((match, matchIndex) => {
          // Create a copy of the original sentence
          let highlightedSentence = sentence;
          let replacedSentence = sentence;

          // Track the count of matches
          let currentMatchCount = 0;

          // Highlight function
          highlightedSentence = highlightedSentence.replace(combinedRegex, (word) => {
            if (currentMatchCount === matchIndex) {
              currentMatchCount++;
              return `<strong>${word}</strong>`;
            }
            currentMatchCount++;
            return word;
          });

          // Replace/Strikethrough function
          currentMatchCount = 0;
          if (isFillerWords) {
            // Strikethrough for filler words
            replacedSentence = replacedSentence.replace(combinedRegex, (word) => {
              if (currentMatchCount === matchIndex) {
                currentMatchCount++;
                return `<strong style="color: var(--sub-primary);"><strike>${word}</strike></strong>`;
              }
              currentMatchCount++;
              return word;
            });
          } else {
            // Regular replacement for non-filler words
            replacedSentence = replacedSentence.replace(combinedRegex, (word) => {
              if (currentMatchCount === matchIndex) {
                currentMatchCount++;
                return `<strong style="color: var(--sub-primary);">${replaceWord}</strong>`;
              }
              currentMatchCount++;
              return word;
            });
          }

          // Add to results with unique ID
          sentences.push({
            uniqueId: `${item.id}-(${matchIndex + 1})`,
            index: index,
            text: highlightedSentence,
            speaker: item.speaker || null,
            startTime: item?.children[0]?.data?.start || 0,
            ...item,
          });

          replaces.push({
            id: `${item.id}-(${matchIndex + 1})`,
            index: index,
            text: replacedSentence,
            speaker: item.speaker || null,
            startTime: item?.children[0]?.data?.start || 0,
          });
        });
      }
    });
    dispatch(updateFindAndReplaceState({ wordSearchedId: sentences[0]?.uniqueId }));
    dispatch(updateScrollToIndexValue(sentences[0]?.index));
    setActiveId(sentences[0]?.uniqueId);
    setActiveIndex(0);
    return [sentences, replaces];
  };

  const extractSentencesBySpeaker = (data, speakerName) => {
    const sentences = [];
    const regexFlags = isCaseSensitive ? 'g' : 'gi';

    if (!speakerName.trim()) {
      return sentences;
    }

    // Split search term into individual words and escape them
    const searchWords = speakerName
      .trim()
      .split(/\s+/)
      .filter((word) => word.length > 0);
    const escapedWords = searchWords.map((word) => word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));

    data.forEach((item, index) => {
      const speaker = item.speaker || '';
      const lowerSpeaker = speaker.toLowerCase();
      const lowerSearchWords = searchWords.map((word) => word.toLowerCase());

      // Check if all search words are present in the speaker name (partial matching)
      const allWordsMatch = lowerSearchWords.every((searchWord) => lowerSpeaker.includes(searchWord));

      if (allWordsMatch) {
        const sentence = item.children.map((child) => child.text).join('');

        // Highlight each matching word in the speaker name
        let highlightedSpeaker = speaker;
        escapedWords.forEach((escapedWord) => {
          const wordRegex = new RegExp(`(${escapedWord})`, regexFlags);
          highlightedSpeaker = highlightedSpeaker.replace(wordRegex, '<strong>$1</strong>');
        });

        // Add to results with unique ID
        sentences.push({
          uniqueId: `${item.id}-speaker`,
          index: index,
          text: sentence,
          speaker: highlightedSpeaker,
          startTime: item?.children[0]?.data?.start || 0,
          ...item,
        });
      }
    });

    if (sentences.length > 0) {
      dispatch(updateFindAndReplaceState({ wordSearchedId: sentences[0]?.uniqueId }));
      dispatch(updateScrollToIndexValue(sentences[0]?.index));
      setActiveId(sentences[0]?.uniqueId);
      setActiveIndex(0);
    }

    return sentences;
  };

  const debouncedExtractSentences = useCallback(
    (findText, slateData, replaceText) => {
      if (findText || isFillerWords) {
        if (mode === 'speaker') {
          // Speaker search mode
          const sentences = extractSentencesBySpeaker(slateData, findText);
          findTextArrayRef.current = sentences;
          setReplacedTextArray([]);
        } else {
          // String search mode
          const [sentences, replaces] = extractSentencesWithTargetWord(slateData, findText, replaceText);
          findTextArrayRef.current = sentences;
          setReplacedTextArray(replaceText || isFillerWords ? replaces : []);
        }

        if (activeIndex !== null && findTextArrayRef.current.length - activeIndex > 0) {
          setActiveId(findTextArrayRef.current[activeIndex].uniqueId);
        }
      } else {
        findTextArrayRef.current = [];
        setReplacedTextArray([]);
      }
    },
    [findText, replaceText, isCaseSensitive, isMatchFullWord, isFillerWords, isCommonErrors, mode],
  );

  useEffect(() => {
    if (timeoutId) clearTimeout(timeoutId);
    const id = setTimeout(() => {
      debouncedExtractSentences(findText, editor.children, replaceText);
      setIsButtonDisable(false);
    }, 800);
    setTimeoutId(id);
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [findText, replaceText, isCaseSensitive, isMatchFullWord, isFillerWords, isCommonErrors, mode, editor.children]);

  useEffect(() => {
    const container = originalScrollRef.current;
    const element = itemRefs.current[activeId];

    if (container && element) {
      const containerTop = container.getBoundingClientRect().top;
      const elementTop = element.getBoundingClientRect().top;

      const scrollOffset = elementTop - containerTop + container.scrollTop;

      container.scrollTo({
        top: scrollOffset,
        behavior: 'smooth',
      });
    }
  }, [activeId]);

  useEffect(() => {
    return () => {
      debouncedUpdateRef.current.cancel();
    };
  }, []);

  const currentIndex = findTextArrayRef?.current?.findIndex((item) => item.uniqueId === wordSearchedId);

  return (
    <div
      style={{
        width: 440,
        maxHeight: '65vh',
        position: 'absolute',
        right: 25,
        backgroundColor: 'white',
        borderRadius: 12,
        height: mode === 'speaker' ? 560 : 740,
        overflow: 'scroll',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
      }}
      className="hide-scrollbar"
    >
      <ClickAwayListener onClickAway={handleClose}>
        <div>
          <div className="preview-dropdown-header">
            Find and Replace
            <HighlightOffRoundedIcon
              onClick={handleClose}
              sx={{
                cursor: 'pointer',
                opacity: 0.5,
                height: '28px',
                width: '28px',
              }}
            />
          </div>
          <Grid container className="preview-dropdown-body" style={{ marginTop: 0 }}>
            {/* Find Input Section */}
            <div>
              <Grid item xs={12} sx={{ paddingTop: '0 !important' }}>
                <RadioGroup
                  row
                  value={mode}
                  onChange={(e) => {
                    setFindText('');
                    setFillterWords(['ah', 'uh', 'um']);
                    setMode(e.target.value);
                    dispatch(
                      updateFindAndReplaceState({
                        mode: e.target.value,
                        selectedIndex: null,
                        word: [],
                        isCommonErrors: false,
                        isCaseSensitive: false,
                        replaceText: '',
                        isFillerWords: false,
                        isMatchFullWord: false,
                      }),
                    );
                    findTextArrayRef.current = [];
                    setReplacedTextArray([]);
                  }}
                  sx={{ marginBottom: 2 }}
                >
                  <FormControlLabel value="string" control={<Radio />} label="Text" />
                  <FormControlLabel value="speaker" control={<Radio />} label="Speaker" />
                </RadioGroup>
              </Grid>
              <Grid item xs={12} sx={{ paddingTop: '0 !important' }}>
                {isCommonErrors ? (
                  <TextField
                    select
                    fullWidth
                    value={commonErrorsOptionRef.current}
                    onChange={(e) => handleChangeCommonErrors(e)}
                    SelectProps={{
                      native: true,
                    }}
                    sx={{
                      maxHeight: '54px',
                      '& select': {
                        fontFamily: "Arial, Helvetica,'Poppins', sans-serif",
                      },
                    }}
                  >
                    <option value="0" style={{ fontFamily: "Arial, Helvetica,'Poppins', sans-serif" }}>
                      Please select
                    </option>
                    <option value=", ," style={{ fontFamily: "Arial, Helvetica,'Poppins', sans-serif" }}>
                      Comma space comma (, ,)
                    </option>
                    <option value="  " style={{ fontFamily: "Arial, Helvetica,'Poppins', sans-serif" }}>
                      Space Space ( )
                    </option>
                    <option value=" ." style={{ fontFamily: "Arial, Helvetica,'Poppins', sans-serif" }}>
                      Space and Fullstop ( .)
                    </option>
                    <option value=",," style={{ fontFamily: "Arial, Helvetica,'Poppins', sans-serif" }}>
                      Comma comma (,,)
                    </option>
                  </TextField>
                ) : (
                  <CustomTextField
                    fullWidth
                    variant="outlined"
                    value={findText}
                    onChange={handleFindWordChange}
                    placeholder={mode === 'speaker' ? 'Enter speaker name' : 'Enter text to find'}
                    autoFocus
                  />
                )}
              </Grid>

              {/* Case Sensitive Checkbox */}
              {mode !== 'speaker' && (
                <Grid item xs={12} paddingY={2}>
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}
                  >
                    <CustomFormControlLabel
                      control={
                        <BpCheckbox
                          checked={isCaseSensitive}
                          onChange={(e) => {
                            dispatch(
                              updateFindAndReplaceState({ isCommonErrors: false, isCaseSensitive: e.target.checked }),
                            );
                            if (findText && !isFillerWords) {
                              isReplaceTextDisabledRef.current = false;
                            } else {
                              if (isFillerWords) {
                                isReplaceTextDisabledRef.current = true;
                              }
                            }
                          }}
                        />
                      }
                      label="Case - Sensitive"
                    />
                    <CustomFormControlLabel
                      control={
                        <BpCheckbox
                          checked={isMatchFullWord}
                          onChange={(e) => {
                            isReplaceTextDisabledRef.current = false;
                            dispatch(
                              updateFindAndReplaceState({
                                isCommonErrors: false,
                                isFillerWords: false,
                                isMatchFullWord: e.target.checked,
                              }),
                            );
                          }}
                        />
                      }
                      label="Match Full Word"
                    />
                    <CustomFormControlLabel
                      control={
                        <BpCheckbox
                          checked={isFillerWords}
                          onChange={(e) => {
                            handleFillerWordsChange(e);
                          }}
                        />
                      }
                      label="Filler Words"
                    />
                    <CustomFormControlLabel
                      control={<BpCheckbox checked={isCommonErrors} onChange={handleCommonErrorsChange} />}
                      label="Common Errors"
                    />
                  </Box>
                </Grid>
              )}

              {/* Replace Input Section */}
              {mode !== 'speaker' && (
                <Grid item xs={12} paddingBottom={2}>
                  <CustomTextField
                    fullWidth
                    variant="outlined"
                    value={replaceText}
                    onChange={(e) => dispatch(updateFindAndReplaceState({ replaceText: e.target.value }))}
                    placeholder="Enter text to replace"
                    disabled={isReplaceTextDisabledRef.current || isFillerWords}
                  />
                </Grid>
              )}

              {/* Original Text List */}
              {mode !== 'speaker' && (
                <Grid item xs={12}>
                  <div className="findreplacebox custom-scroll" ref={originalScrollRef}>
                    <ul className="replaceText">
                      {findTextArrayRef.current?.length > 0 ? (
                        findTextArrayRef.current.map((text, index) => (
                          <Accordion
                            key={index}
                            ref={(el) => {
                              if (el) itemRefs.current[text.uniqueId] = el;
                            }}
                            className={`replaceTextItem ${activeIndex === index ? 'active' : ''}`}
                            onClick={() => {
                              handleClickReplace(index);
                              setActiveId(text.uniqueId);
                              dispatch(updateFindAndReplaceState({ selectedIndex: text.index }));
                              dispatch(updateFindAndReplaceState({ wordSearchedId: text.uniqueId }));
                              dispatch(updateScrollToIndexValue(text.index));
                            }}
                            expanded={activeIndex === index}
                            style={{
                              margin: 0,
                              boxShadow: 'none',
                              backgroundColor: activeIndex === index ? 'rgb(140 82 254 / 10%)' : 'transparent',
                            }}
                          >
                            <AccordionSummary
                              sx={{
                                padding: 0,
                                minHeight: '48px !important',
                                '& .MuiAccordionSummary-content': {
                                  margin: '0 !important',
                                },
                                '& .MuiAccordionSummary-expandIconWrapper': {
                                  margin: 0,
                                },
                              }}
                            >
                              <span
                                className="replaceTextLink"
                                dangerouslySetInnerHTML={{
                                  __html: getSentenceAroundHighlightedWordWithTag(text.text, 3, replaceText),
                                }}
                              />
                            </AccordionSummary>
                          </Accordion>
                        ))
                      ) : (
                        <div className="replaceTextLink find-replace-no-data">No matches found</div>
                      )}
                    </ul>
                  </div>
                </Grid>
              )}

              {/* Speaker Results List */}
              {mode === 'speaker' && (
                <Grid item xs={12}>
                  <div className="findreplacebox custom-scroll" ref={originalScrollRef}>
                    <ul className="replaceText">
                      {findTextArrayRef.current.length > 0 ? (
                        findTextArrayRef.current.map((text, index) => (
                          <Accordion
                            key={index}
                            ref={(el) => {
                              if (el) itemRefs.current[text.uniqueId] = el;
                            }}
                            className={`replaceTextItem ${activeIndex === index ? 'active' : ''}`}
                            onClick={() => {
                              handleClickReplace(index);
                              setActiveId(text.uniqueId);
                              dispatch(updateFindAndReplaceState({ selectedIndex: text.index }));
                              dispatch(updateFindAndReplaceState({ wordSearchedId: text.uniqueId }));
                              dispatch(updateScrollToIndexValue(text.index));
                            }}
                            expanded={activeIndex === index}
                            style={{
                              margin: 0,
                              boxShadow: 'none',
                              backgroundColor: activeIndex === index ? 'rgb(140 82 254 / 10%)' : 'transparent',
                            }}
                          >
                            <AccordionSummary
                              sx={{
                                padding: 0,
                                minHeight: '48px !important',
                                '& .MuiAccordionSummary-content': {
                                  margin: '0 !important',
                                },
                                '& .MuiAccordionSummary-expandIconWrapper': {
                                  margin: 0,
                                },
                              }}
                            >
                              <div className="replaceTextLink">
                                <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                                  <span dangerouslySetInnerHTML={{ __html: text.speaker }} />
                                </div>
                                <div style={{ fontSize: '0.9em', color: '#666' }}>
                                  {text.text.length > 100 ? `${text.text.substring(0, 100)}...` : text.text}
                                </div>
                              </div>
                            </AccordionSummary>
                          </Accordion>
                        ))
                      ) : (
                        <div className="replaceTextLink find-replace-no-data">
                          {findText.trim() ? 'No speakers found' : 'Enter speaker name to search'}
                        </div>
                      )}
                    </ul>
                  </div>
                </Grid>
              )}

              {/* Buttons Section */}
              <Grid
                item
                xs={12}
                sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                paddingY={2}
              >
                {mode !== 'speaker' && (
                  <>
                    <CustomButton
                      variant="secondary"
                      size="large"
                      disabled={
                        activeIndex === null ||
                        !replacedTextArray ||
                        replacedTextArray?.length === 0 ||
                        activeId === null ||
                        isButtonsDisable
                      }
                      onClick={() => handleReplace(activeId)}
                    >
                      Replace
                    </CustomButton>
                    <CustomButton
                      variant="secondary"
                      size="large"
                      onClick={(e) => {
                        e.preventDefault();
                        handleReplaceAll();
                      }}
                      disabled={!replacedTextArray || replacedTextArray?.length === 0 || isButtonsDisable}
                    >
                      Replace All
                    </CustomButton>
                    <Typography>
                      {findTextArrayRef?.current?.length > 0 && (
                        <>
                          {currentIndex + 1} of {findTextArrayRef?.current?.length || 0}
                        </>
                      )}
                    </Typography>
                  </>
                )}
                {mode === 'speaker' && findTextArrayRef?.current?.length > 0 && (
                  <Typography>
                    {currentIndex + 1} of {findTextArrayRef?.current?.length || 0} speakers found
                  </Typography>
                )}
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{
                    border: '1px solid #ccc',
                    borderRadius: 2,
                    padding: '1px 4px',
                    marginLeft: mode === 'speaker' ? 'auto' : 0,
                  }}
                >
                  <KeyboardArrowUpIcon
                    sx={{ cursor: 'pointer' }}
                    onClick={() => {
                      if (findTextArrayRef?.current.length > 0) {
                        const prevIndex = activeIndex - 1 < 0 ? findTextArrayRef.current.length - 1 : activeIndex - 1;
                        setActiveIndex(prevIndex);
                        setActiveId(findTextArrayRef.current[prevIndex].uniqueId);
                        dispatch(
                          updateFindAndReplaceState({ selectedIndex: findTextArrayRef.current[prevIndex].index }),
                        );
                        dispatch(
                          updateFindAndReplaceState({ wordSearchedId: findTextArrayRef.current[prevIndex].uniqueId }),
                        );
                        dispatch(updateScrollToIndexValue(findTextArrayRef.current[prevIndex].index));
                      }
                    }}
                  />
                  <Divider orientation="vertical" flexItem />
                  <KeyboardArrowDownIcon
                    sx={{ cursor: 'pointer' }}
                    onClick={() => {
                      if (findTextArrayRef?.current.length > 0) {
                        const nextIndex = activeIndex + 1 >= findTextArrayRef.current.length ? 0 : activeIndex + 1;
                        setActiveIndex(nextIndex);
                        setActiveId(findTextArrayRef.current[nextIndex].uniqueId);
                        dispatch(
                          updateFindAndReplaceState({ selectedIndex: findTextArrayRef.current[nextIndex].index }),
                        );
                        dispatch(
                          updateFindAndReplaceState({ wordSearchedId: findTextArrayRef.current[nextIndex].uniqueId }),
                        );
                        dispatch(updateScrollToIndexValue(findTextArrayRef.current[nextIndex].index));
                      }
                    }}
                  />
                </Stack>
              </Grid>
            </div>
          </Grid>
        </div>
      </ClickAwayListener>
    </div>
  );
};

export default FindAndReplaceModal;
